/**
 * ExploreCategorySingleExercises - Single exercises section
 * Created by neo on 21.01.26
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container } from 'reactstrap';
import { Link } from 'react-router-dom';
import { ExploreCategory } from '../../Model/Explore/ExploreCategory';
import { useRootStore } from '../../Store/useRootStore';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useLocalized } from '../../Hooks/useLocalized';
import { Theme } from '../../Theme/Theme';
import { MarkdownRenderer } from '../Markdown/MarkdownRenderer';
import { ExerciseVariation } from '../../Model/ProgramPortfolio/ExerciseVariation';
import { ExerciseTileCard } from './ExerciseTileCard';
import { ExerciseVideoModal } from './ExerciseVideoModal';
import { ExerciseVariationApi } from '../../Model/ProgramPortfolio/ExerciseVariation';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const Section = styled.div`
  padding: 32px 0;
  background: white;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  gap: 16px;
`;

const TitleGroup = styled.div`
  flex: 1;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  color: ${Theme.Colors.textColor};

  @media (max-width: 600px) {
    font-size: 1.25rem;
  }
`;

const Description = styled.div`
  font-size: 0.95rem;
  color: ${Theme.Colors.charcoal};
  margin-bottom: 20px;
  line-height: 1.6;
`;

const ArrowButton = styled(Link)`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${Theme.Colors.lighterGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 18px;
  text-decoration: none;
  color: ${Theme.Colors.textColor};
  transition: all 0.2s ease;

  &:hover {
    background: ${Theme.Colors.primary};
    color: white;
  }
`;

const HorizontalScroll = styled.div`
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding: 8px 24px 16px 24px;
  margin: 0 -24px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const CardWrapper = styled.div`
  flex: 0 0 200px;
  min-width: 200px;

  @media (max-width: 600px) {
    flex: 0 0 160px;
    min-width: 160px;
  }
`;

const SkeletonCard = styled.div`
  flex: 0 0 200px;
  min-width: 200px;

  @media (max-width: 600px) {
    flex: 0 0 160px;
    min-width: 160px;
  }
`;

const SkeletonImage = styled.div`
  aspect-ratio: 1;
  border-radius: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const SkeletonText = styled.div<{ width?: string }>`
  height: 12px;
  width: ${({ width }) => width || '100%'};
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  margin-top: 10px;
`;

export type ExploreCategorySingleExercisesProps = {
  category: ExploreCategory;
  linkPrefix?: string;
};

export const ExploreCategorySingleExercises: React.FC<ExploreCategorySingleExercisesProps> = observer(
  ({ category, linkPrefix = '/move/' }) => {
    const { language } = useRootStore();
    const { t } = useTrans();

    const [exercises, setExercises] = useState<ExerciseVariation[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedExercise, setSelectedExercise] = useState<ExerciseVariation | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const title = useLocalized(category.name);
    const description = useLocalized(category.description);

    useEffect(() => {
      setLoading(true);
      ExerciseVariationApi.random({
        tags: category?.identifier ? [category.identifier] : [],
        size: 10,
      })
        .then((result) => setExercises(result))
        .finally(() => setLoading(false));
    }, [category]);

    const handleExerciseClick = useCallback((exercise: ExerciseVariation) => {
      setSelectedExercise(exercise);
      setIsModalOpen(true);
    }, []);

    const handleCloseModal = useCallback(() => {
      setIsModalOpen(false);
      setSelectedExercise(null);
    }, []);

    const renderSkeletonCards = () =>
      Array.from({ length: 5 }).map((_, i) => (
        <SkeletonCard key={`skeleton-${i}`}>
          <SkeletonImage />
          <SkeletonText width="80%" />
        </SkeletonCard>
      ));

    if (!loading && exercises.length === 0) {
      return null;
    }

    return (
      <Section>
        <Container>
          <SectionHeader>
            <TitleGroup>
              <Title>{title}</Title>
            </TitleGroup>
            <ArrowButton to={`${linkPrefix}${category.identifier}`}>→</ArrowButton>
          </SectionHeader>
          {description && (
            <Description>
              <MarkdownRenderer text={description} />
            </Description>
          )}
          <HorizontalScroll>
            {loading
              ? renderSkeletonCards()
              : exercises.map((exercise) => (
                  <CardWrapper key={exercise.id}>
                    <ExerciseTileCard exercise={exercise} onClick={handleExerciseClick} />
                  </CardWrapper>
                ))}
          </HorizontalScroll>
        </Container>
        <ExerciseVideoModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          exercise={selectedExercise}
        />
      </Section>
    );
  },
);
